import type { FormInitialValues } from '../typings';
import { sendBeaconRequests, type BeaconRequest } from './beacon';
import {
  getUnloadRequestBuilder,
  listSaveQueueKeys,
  peekSaveQueue,
} from './section-save-queue';

export type FlushSaveQueuesOnUnloadDeps = {
  baseUrl: string;
  token: string | null;
  /** Injectable for tests. */
  send?: typeof sendBeaconRequests;
};

type PlannedEdit = {
  key: string;
  fieldId: string;
  value: FormInitialValues[string];
};

// Two trackers can be co-mounted on one page — a DynamicForm nested inside another
// form's page — each registering its own `pagehide` listener bound to this same
// function (see use-form-change-tracker.ts).
// Without this guard a second call would start a fresh 64KB budget on top of the first,
// breaking the "one call, one shared budget" invariant below. Module scope, not a ref:
// the two callers are unrelated components with no shared tree to hold one in.
let hasFlushedThisTeardown = false;

// The only way a page that fired `pagehide` comes back to life is a bfcache restore,
// signalled by `pageshow` with `persisted: true`. That is also the only case where a
// later `pagehide` on this same page is a genuine new teardown rather than a duplicate
// call for the one just handled — so it is the only place this guard may reset.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      hasFlushedThisTeardown = false;
    }
  });
}

/** Test-only: undoes the one-flush-per-teardown guard between tests. */
export const resetUnloadFlushGuardForTests = (): void => {
  hasFlushedThisTeardown = false;
};

/**
 * Hands every queue's unacknowledged edits to the keepalive transport and puts back
 * whatever did not fit. Fire-and-forget by design: nothing here can read a response, so
 * nothing here participates in the ack contract.
 */
export const flushSaveQueuesOnUnload = ({
  baseUrl,
  token,
  send = sendBeaconRequests,
}: FlushSaveQueuesOnUnloadDeps): void => {
  if (hasFlushedThisTeardown) return;
  hasFlushedThisTeardown = true;

  const planned: PlannedEdit[] = [];
  const requests: BeaconRequest[] = [];

  listSaveQueueKeys().forEach(key => {
    const builder = getUnloadRequestBuilder(key);
    // Without a builder nothing here knows this form's endpoints. Leave the edits in the
    // queue rather than taking values we cannot send.
    if (!builder) return;

    const queue = peekSaveQueue(key);
    if (!queue) return;

    const changes = queue.takeUnacknowledged();
    if (Object.keys(changes).length === 0) return;

    // A field the planner produces no request for — a staged child, a synthetic key — is
    // dropped here exactly as the axios path drops it.
    builder(changes).forEach(({ fieldId, request }) => {
      planned.push({ key, fieldId, value: changes[fieldId] });
      requests.push(request);
    });
  });

  if (requests.length === 0) return;

  // One call, so the 64KB keepalive budget is shared across every form on the page.
  const sent = send(requests, {
    baseUrl,
    token,
    // A request within budget can still fail (offline, server error) on a page that
    // survives pagehide. `sent` below only tells us it was issued, not that it landed —
    // this is the other half of that, put back exactly like the over-budget tail is.
    onFailed: index => {
      const edit = planned[index];
      peekSaveQueue(edit.key)?.enqueue(edit.fieldId, edit.value);
    },
  });

  // If this was a bfcache hide rather than a real teardown, the ordinary flush still
  // has whatever the transport could not take.
  planned.slice(sent).forEach(({ key, fieldId, value }) => {
    peekSaveQueue(key)?.enqueue(fieldId, value);
  });
};
